import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Paper
} from "@material-ui/core";
import clsx from "clsx";
import { useFormik } from "formik";
import { arrayMoveImmutable } from "array-move";
import useStyles from "@/styles/dashboardStyle";
import NubisDevicesAmount from "@/components/dashboard/NubisDevicesAmount";
import KioskMode from "@/components/dashboard/KioskMode";
import OperationSystemVersion from "@/components/dashboard/OperationSystemVersion";
import DeviceHealthStatus from "@/components/dashboard/DeviceHealthStatus";
import OfflineDays from "@/components/dashboard/OfflineDays";
import OraVersionDistribution from "@/components/dashboard/OraVersionDistribution";
import HardwardModels from "@/components/dashboard/HardwareModels";
import VNCAddressForMultipleUsers from "@/components/dashboard/VNCAddressForMultipleUsers";
import TaskFailedDatatable from "@/components/dashboard/TaskFailedDatatable";
import { BreadCrumbs } from "@/components/device/BreadCrumbs";
import ErrorBoundary from "@/helpers/errorBoundary";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { FormItem } from "@/components/Form";
import TagList from "@/components/FormFields/TagList";
import { useTranslation } from "@/langs/useTranslation";
import CompanyChangeHandleWrapper from "@/components/CompanyChangeHandleWrapper";
import BlockVisibilityManagerModal from "./BlockVisibilityManager/Modal";
import { Settings } from "@material-ui/icons";
import {
  getDashboradSortResultApi,
  saveDashboradSortResultApi
} from "@/apis/apiCollection";
import LoadingIndicator from "@/components/commons/LoadingIndicator";
import SubmitButton from "@/components/commons/SubmitButton";
import AlarmDeviceDatatable from "@/components/dashboard/AlarmDeviceDatatable";
import FailedScheduleDeviceDatatable from "@/components/dashboard/FailedScheduleDeviceDatatable";
import AlarmDevicePolicy from "@/components/dashboard/AlarmDevicePolicy";
import DeviceGroups from "@/components/FormFields/DeviceGroups/DeviceGroups";
import Marquee from "react-fast-marquee";
import { Alert } from "antd";
import { IconAnnouncement } from "@/components/Icon";
import { getMapProvider } from "@/components/map";
import { updateNotificationAPI } from "@/apis/announcementApi";
import { isCNDomain } from "@/helpers/utils";
import HasPermission from "@/components/commons/HasPermission";
import { checkHasPermission } from "@/helpers/checkHasPermission";

const isCN = isCNDomain();

const initialQueryValues = {
  group_ids: [],
  tag_ids: [],
  hasSubCompanies: 0
};

export const QueryContext = React.createContext(initialQueryValues);

export const useQueryParams = () => {
  const queryParams = useContext(QueryContext);
  const queryString = useMemo(() => {
    const groupIds = queryParams.group_ids?.length
      ? `&groupIds=${queryParams.group_ids.join(",")}`
      : "";
    const tagIds = queryParams.tag_ids?.length
      ? `&tagIds=${queryParams.tag_ids.join(",")}`
      : "";
    const hasSubCompanies =
      queryParams.hasSubCompanies === 1 ? "&hasSubCompanies=1" : "";

    return groupIds + tagIds + hasSubCompanies;
  }, [queryParams]);

  return [queryParams, queryString];
};

const GridContainer = SortableContainer(({ className, children }) => {
  return (
    <Grid container spacing={2} alignItems="stretch" className={className}>
      {children}
    </Grid>
  );
});

const ItemMap = SortableElement(() => {
  const Map = getMapProvider();

  return (
    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
      <Paper elevation={0} square>
        <Map height={300} />
      </Paper>
    </Grid>
  );
});

const ItemNubisDevicesAmount = SortableElement(() => {
  return (
    <Grid item xl={3} lg={4} md={6} sm={12} xs={12}>
      <Paper elevation={0} style={{ padding: 0, background: "none" }}>
        <NubisDevicesAmount
          cardTitleTypographyVariant="h6"
          cardSubtitleTypographyVariant="subtitle2"
          cardHighlightTypographyVariant="h4"
        />
      </Paper>
    </Grid>
  );
});

const ItemAlarmDevicePolicy = SortableElement(({ title }) => {
  return (
    <Grid item xl={3} lg={4} md={6} sm={12} xs={12}>
      <AlarmDevicePolicy title={title} />
    </Grid>
  );
});

const ItemKioskMode = SortableElement(({ title }) => {
  return (
    <Grid item xl={3} lg={4} md={6} sm={12} xs={12}>
      <KioskMode
        title={title}
        cardTitleTypographyVariant="h6"
        cardSubtitleTypographyVariant="subtitle2"
        cardHighlightTypographyVariant="h4"
      />
    </Grid>
  );
});

const ItemOperationSystemVersion = SortableElement(({ title }) => {
  return (
    <Grid item xl={3} lg={4} md={6} sm={12} xs={12}>
      <OperationSystemVersion
        title={title}
        cardTitleTypographyVariant="h6"
        cardSubtitleTypographyVariant="subtitle2"
        cardHighlightTypographyVariant="h4"
      />
    </Grid>
  );
});

const ItemOfflineDays = SortableElement(({ title }) => {
  return (
    <Grid item xl={3} lg={4} md={6} sm={12} xs={12}>
      <OfflineDays
        title={title}
        cardTitleTypographyVariant="h6"
        cardSubtitleTypographyVariant="subtitle2"
        cardHighlightTypographyVariant="h4"
      />
    </Grid>
  );
});

const ItemOraVersionDistribution = SortableElement(({ title }) => {
  return (
    <Grid item xl={3} lg={4} md={6} sm={12} xs={12}>
      <OraVersionDistribution
        title={title}
        cardTitleTypographyVariant="h6"
        cardSubtitleTypographyVariant="subtitle2"
        cardHighlightTypographyVariant="h4"
      />
    </Grid>
  );
});

const ItemAlarmDeviceDatatable = SortableElement(({ title }) => {
  return (
    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
      <AlarmDeviceDatatable title={title} />
    </Grid>
  );
});

const ItemFailedScheduleDeviceDatatable = SortableElement(({ title }) => {
  return (
    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
      <FailedScheduleDeviceDatatable title={title} />
    </Grid>
  );
});

const ItemTaskFailedDatatable = SortableElement(({ title }) => {
  return (
    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
      <TaskFailedDatatable title={title} cardTitleTypographyVariant="h6" />
    </Grid>
  );
});

const ItemHardwardModels = SortableElement(({ title }) => {
  return (
    <HasPermission permission="hardware_models">
      <Grid item xl={4} lg={4} md={12} sm={12} xs={12}>
        <HardwardModels
          title={title}
          cardTitleTypographyVariant="h6"
          cardSubtitleTypographyVariant="subtitle2"
          cardHighlightTypographyVariant="h4"
        />
      </Grid>
    </HasPermission>
  );
});

const ItemDeviceHealthStatus = SortableElement(({ title }) => {
  const classes = useStyles();

  return (
    <Grid
      item
      xl={8}
      lg={8}
      md={12}
      sm={12}
      xs={12}
      className={classes.deviceHealthStatusGrid}
    >
      <DeviceHealthStatus
        title={title}
        cardTitleTypographyVariant="h6"
        cardSubtitleTypographyVariant="subtitle2"
        cardHighlightTypographyVariant="h4"
      />
    </Grid>
  );
});

const ItemVNCAddressForMultipleUsers = SortableElement(({ title }) => {
  return (
    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
      <VNCAddressForMultipleUsers
        title={title}
        cardTitleTypographyVariant="h6"
      />
    </Grid>
  );
});

const QueryForm = ({ onSubmit }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { values, handleChange, handleSubmit, handleReset, setFieldValue } =
    useFormik({
      initialValues: initialQueryValues,
      onSubmit: () => doQuery()
    });

  const getSearchParams = isReset => {
    const formValues = isReset ? initialQueryValues : values;
    return {
      group_ids: formValues.group_ids.length ? formValues.group_ids : undefined,
      tag_ids: formValues.tag_ids.length ? formValues.tag_ids : undefined,
      hasSubCompanies: formValues.hasSubCompanies ? 1 : 0
    };
  };

  const doQuery = reset => {
    const params = getSearchParams(reset);
    onSubmit?.(params);
  };

  const resetQuery = () => {
    handleReset();
    doQuery(true);
  };

  useEffect(() => {
    if (
      (values.group_ids.length > 0 || values.tag_ids.length > 0) &&
      values.hasSubCompanies
    ) {
      setFieldValue("hasSubCompanies", 0);
    }
  }, [values]);

  return (
    <form onSubmit={handleSubmit}>
      <div className={classes.queryForm}>
        <FormItem label={t({ defaultMessage: "门店" })}>
          <DeviceGroups
            name="group_ids"
            value={values.group_ids}
            onChange={handleChange}
            style={{ width: 200 }}
          />
        </FormItem>
        <FormItem label={t({ defaultMessage: "标签" })}>
          <TagList
            name="tag_ids"
            expandCompanies={Boolean(values.hasSubCompanies)}
            value={values.tag_ids}
            onChange={handleChange}
            style={{ width: 200 }}
          />
        </FormItem>
        <FormItem>
          <FormControlLabel
            name="hasSubCompanies"
            checked={Boolean(values.hasSubCompanies)}
            onChange={handleChange}
            label={t({ defaultMessage: "展开子公司" })}
            disabled={values.group_ids.length > 0 || values.tag_ids.length > 0}
            control={<Checkbox />}
          />
        </FormItem>
        <FormItem>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            style={{ marginRight: 8 }}
          >
            {t({ defaultMessage: "查询" })}
          </Button>
          <Button variant="outlined" onClick={resetQuery}>
            {t({ defaultMessage: "重置" })}
          </Button>
        </FormItem>
      </div>
    </form>
  );
};

function serializeFromObject(sortObj) {
  return sortObj
    .map(item => `${item.widgetIndex}|${item.visible ? 1 : 0}`)
    .join(",");
}

function deSerializeFromString(sortStr) {
  return sortStr.split(",").map(item => {
    const split = item.split("|").map(Number);
    return {
      widgetIndex: split[0],
      visible: split[1] === 1
    };
  });
}

function mergeStrategy(defaultOrder, savedOrder) {
  const newAddedModules = [];
  // 从默认排序找有没有新增的模块
  defaultOrder.forEach((item, index) => {
    if (!savedOrder.find(order => order.widgetIndex === item.widgetIndex)) {
      newAddedModules.push([item, index]);
    }
  });
  if (newAddedModules.length) {
    newAddedModules.forEach(m => {
      // 从服务端已保存的排序插入新增的模块
      savedOrder.splice(m[1], 0, m[0]);
    });
  }
  return savedOrder;
}

// 默认排序
const defaultOrder = [
  { widgetIndex: 0, visible: !isCN },
  { widgetIndex: 1, visible: true },
  { widgetIndex: 2, visible: true },
  { widgetIndex: 12, visible: true },
  { widgetIndex: 3, visible: true },
  { widgetIndex: 4, visible: true },
  { widgetIndex: 5, visible: true },
  { widgetIndex: 6, visible: true },
  { widgetIndex: 7, visible: true },
  { widgetIndex: 8, visible: true },
  { widgetIndex: 9, visible: true },
  { widgetIndex: 10, visible: true },
  { widgetIndex: 11, visible: true }
];

const Dashboard = () => {
  const classes = useStyles();
  const { t, langConf } = useTranslation("dashboard");

  const [order, setOrder] = useState(defaultOrder);
  const [queryParams, setQueryParams] = useState(initialQueryValues);
  const [sortable, setSortable] = useState(false);
  const [showManager, setShowManager] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const [announcement, setAnnouncement] = useState(null);

  // 注意：后续新增的组件一定要放在数组最后
  const WidgetList = [
    { title: t({ defaultMessage: "地图" }), widget: ItemMap },
    {
      title: t({ defaultMessage: "Nubis设备" }),
      widget: ItemNubisDevicesAmount
    },
    {
      title: t({ defaultMessage: "报警规则列表" }),
      widget: ItemAlarmDevicePolicy
    },
    {
      title: t("dashboard.operationSystemVersionTitle"),
      widget: ItemOperationSystemVersion
    },
    {
      title: t("dashboard.offlineDeviceStatus"),
      widget: ItemOfflineDays
    },
    {
      title: t("dashboard.oraVersionDistribution", { ora: langConf.deviceCenter?.oraText || "Ora" }),
      widget: ItemOraVersionDistribution
    },
    {
      title: t({ defaultMessage: "设备报警列表" }),
      widget: ItemAlarmDeviceDatatable
    },
    {
      title: t({ defaultMessage: "设备排程失败列表" }),
      widget: ItemFailedScheduleDeviceDatatable
    },
    {
      title: t({ defaultMessage: "任务失败列表" }),
      widget: ItemTaskFailedDatatable
    },
    {
      title: t("dashboard.hardwareModelsTitle"),
      widget: ItemHardwardModels,
      permission: "hardware_models"
    },
    {
      title: t("dashboard.deviceHealthStatusTitle"),
      widget: ItemDeviceHealthStatus
    },
    {
      title: t("dashboard.vncAddressForMultipleUsersTitle"),
      widget: ItemVNCAddressForMultipleUsers
    },
    {
      title: t({ defaultMessage: "Kiosk模式" }),
      widget: ItemKioskMode
    }
  ];

  const isWidgetExist = item => {
    return !!WidgetList[item.widgetIndex];
  };

  const configList = useMemo(() => {
    return order.map(item => ({
      widgetIndex: item.widgetIndex,
      visible: item.visible,
      title: WidgetList[item.widgetIndex]?.title
    }));
  }, [order]);

  const defaultConfigList = useMemo(() => {
    return defaultOrder.map(item => ({
      widgetIndex: item.widgetIndex,
      visible: item.visible,
      title: WidgetList[item.widgetIndex]?.title
    }));
  }, []);

  const visibleItems = useMemo(() => {
    return order.filter(item => item.visible);
  }, [order]);

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    setOrder(preOrder => {
      const newVisibleItems = arrayMoveImmutable(
        visibleItems,
        oldIndex,
        newIndex
      );
      return preOrder.map(item => {
        if (!item.visible) return item;
        return newVisibleItems.shift();
      });
    });
  };

  const handleQuery = params => {
    setQueryParams(params);
  };

  const getSortResult = async () => {
    try {
      const { dashboardModuleOrder } = await getDashboradSortResultApi();
      if (dashboardModuleOrder) {
        const savedOrder =
          deSerializeFromString(dashboardModuleOrder).filter(isWidgetExist);
        setOrder(mergeStrategy(order, savedOrder));
      }
    } finally {
      setLoading(false);
    }
  };

  const updateSortResult = async order => {
    try {
      setUpdating(true);
      await saveDashboradSortResultApi({
        dashboardModuleOrder: serializeFromObject(order)
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleEdit = async () => {
    if (sortable) {
      await updateSortResult(order);
    }
    setSortable(prev => !prev);
  };

  const handleShowManager = () => {
    setShowManager(true);
  };

  const handleManagerClose = () => {
    setShowManager(false);
  };

  const handleManagerConfirm = async list => {
    await updateSortResult(list);
    setOrder(list);
    setShowManager(false);
  };

  useEffect(() => {
    getSortResult();
  }, []);

  useEffect(() => {
    window.__diyFunction__setAnnouncement = setAnnouncement;
    setAnnouncement(window.__diyFunction__announcement);
  }, []);

  return (
    <ErrorBoundary>
      <div className={classes.root}>
        <BreadCrumbs pageName="dashboard" />
        {loading ? (
          <LoadingIndicator centered />
        ) : (
          <>
            {Boolean(announcement) && announcement?.isScroll && (
              <Alert
                banner
                closable
                onClose={async () => {
                  await updateNotificationAPI([
                    window.__diyFunction__announcement_notificationId
                  ]);
                  if (window.__diyFunction__announcement_refresh)
                    window.__diyFunction__announcement_refresh();
                }}
                style={{
                  borderRadius: 5,
                  marginBottom: 15,
                  backgroundColor: "rgba(247,134,42,0.2)",
                  color: "#F7862A"
                }}
                showIcon
                icon={<IconAnnouncement color="#F7862A" />}
                message={
                  // announcement?.isScroll ? (
                  <Marquee
                    pauseOnHover
                    gradient={false}
                    style={{ color: "#F7862A" }}
                  >
                    {announcement.title}: {announcement.message}
                  </Marquee>
                  // ) : (
                  //   <span style={{ color: "#F7862A" }}>
                  //     {announcement.title}: {announcement.message}
                  //   </span>
                  // )
                }
              />
            )}
            <div className={classes.top}>
              <QueryForm onSubmit={handleQuery} />
              <div>
                <IconButton
                  onClick={handleShowManager}
                  style={{ marginRight: 16 }}
                >
                  <Settings />
                </IconButton>
                <SubmitButton
                  variant="contained"
                  color="primary"
                  loading={updating}
                  onClick={handleEdit}
                >
                  {sortable
                    ? t({ defaultMessage: "保存" })
                    : t({ defaultMessage: "编辑" })}
                </SubmitButton>
              </div>
            </div>
            <QueryContext.Provider value={queryParams}>
              <CompanyChangeHandleWrapper>
                <GridContainer
                  axis="xy"
                  distance={2}
                  className={clsx(sortable && classes.sortable)}
                  helperClass={clsx(classes.sortableElement, classes.root)}
                  onSortEnd={onSortEnd}
                >
                  {visibleItems.map((orderItem, index) => {
                    const item = WidgetList[orderItem.widgetIndex];
                    if (item.permission) {
                      if (!checkHasPermission(item.permission)) return null
                    }
                    if (item) {
                      const Widget = item.widget;
                      return (
                        <Widget
                          disabled={!sortable}
                          index={index}
                          key={item.title}
                          title={item.title}
                        />
                      );
                    }
                    return null;
                  })}
                </GridContainer>
              </CompanyChangeHandleWrapper>
            </QueryContext.Provider>
            <BlockVisibilityManagerModal
              open={showManager}
              defaultList={defaultConfigList}
              list={configList}
              onConfirm={handleManagerConfirm}
              onClose={handleManagerClose}
            />
          </>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default Dashboard;
